import { createApi } from '@reduxjs/toolkit/query/react'
import { BaseRequest, BaseResponse } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { EmailFilter, EmailFilterPayload } from '../../models/EmailFilter'
import { paginate } from '../utils'

const _BASE_URL = import.meta.env.REACT_APP_INTEGRATIONS_SERVICE_API_BASE_URL!

export interface CreateEmailFilterPayload {
  name: string
  isDefault?: boolean
  filter: EmailFilterPayload
}

export interface CreateEmailFilterRequest extends BaseRequest {
  payload: CreateEmailFilterPayload
}

export interface UpdateEmailFilterPayload {
  name?: string
  isDefault?: boolean
  filter?: EmailFilterPayload
}

export interface UpdateEmailFilterRequest extends BaseRequest {
  emailFilterId: string
  payload: UpdateEmailFilterPayload
}

export interface DeleteEmailFilterRequest extends BaseRequest {
  emailFilterId: string
}

export const emailFilterApi = createApi({
  reducerPath: 'emailFilterApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['EmailFilter'],
  endpoints: (builder) => ({
    getAllEmailFilters: builder.query<BaseResponse<EmailFilter[]>, BaseRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<EmailFilter>(
          {
            url: `/accounts/${request.accountId}/email-filters`,
          },
          fetchWithBQ,
        )
      },
      providesTags: ['EmailFilter'],
    }),
    createEmailFilter: builder.mutation<EmailFilter, CreateEmailFilterRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/email-filters/`,
        method: 'POST',
        body: request.payload,
      }),
      transformResponse: (response: BaseResponse<EmailFilter>) => {
        return response.data
      },
      invalidatesTags: ['EmailFilter'],
    }),
    updateEmailFilter: builder.mutation<EmailFilter, UpdateEmailFilterRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/email-filters/${request.emailFilterId}`,
        method: 'PATCH',
        body: request.payload,
      }),
      transformResponse: (response: BaseResponse<EmailFilter>) => {
        return response.data
      },
      invalidatesTags: ['EmailFilter'],
    }),
    deleteEmailFilter: builder.mutation<void, DeleteEmailFilterRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/email-filters/${request.emailFilterId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EmailFilter'],
    }),
  }),
})

export const { useLazyGetAllEmailFiltersQuery, useCreateEmailFilterMutation, useUpdateEmailFilterMutation, useDeleteEmailFilterMutation } = emailFilterApi
